import React, { FC, useContext } from "react";
import { Table, Typography } from "antd";
import { AccruedTime, formatPrice, formatTime, RiskLevel } from "../../data-types";
import styles from "./accrued-time-log.css";
import { ColumnType } from "antd/lib/table";
import { Colors } from "@design-system";
import { GlobalContext } from "~/context/global-context";
import { useTaxonomyLabel } from "~/context/taxonomy-context";

const {
  ["container"]: containerClassName,
  ["header"]: headerClassName,
  ["time-recorded"]: timeRecordedClassName,
} = styles as any;

export type AccruedTimeLogProps = {
  loading: boolean;
  matterTitle: string;
  clientName: string;
  accruedTimes: AccruedTime[];
  currentGpm: number;
  currentGpmRiskLevel: RiskLevel;
  targetGpm: number;
  gpmSource: string;
  currencySymbol: string;
};

const colourByLevel = (riskLevel: RiskLevel) => {
  switch (riskLevel) {
    case RiskLevel.None:
    case RiskLevel.LowRisk:
      return Colors.Success;
    case RiskLevel.MediumRisk:
      return Colors.Warning;
    case RiskLevel.HighRisk:
    case RiskLevel.Issue:
      return Colors.Error;
  }
};

export const AccruedTimeLog: FC<AccruedTimeLogProps> = ({
  loading,
  currentGpm,
  gpmSource,
  targetGpm,
  accruedTimes,
  currentGpmRiskLevel,
  currencySymbol,
}) => {
  const { showGpm } = useContext(GlobalContext);
  const taxonomy = useTaxonomyLabel();
  return (
    <div className={containerClassName}>
      {showGpm && (
        <div className={headerClassName}>
          <div>
            <Typography.Paragraph>
              The actual GPM for this matter is
              <span style={{ color: colourByLevel(currentGpmRiskLevel), fontWeight: "bold" }}>
                {" " + Math.round(currentGpm)}%
              </span>
              , the required GPM is {targetGpm}% which has been set at a {gpmSource} Level.
            </Typography.Paragraph>
          </div>
        </div>
      )}
      <div className={timeRecordedClassName}>
        <h4>Time Recorded</h4>
        <Table
          columns={doColumns(currencySymbol, taxonomy.FeeEarner, taxonomy.FeeEarnerLevel)}
          dataSource={accruedTimes}
          bordered={false}
          loading={loading}
          scroll={{ x: "max-content" }}
          pagination={{ pageSize: 5, hideOnSinglePage: true }}
          rowKey={({ feeEarnerName, feeEarnerLevelDescription }, index) =>
            index ? index : feeEarnerName ? feeEarnerName : feeEarnerLevelDescription
          }
          locale={{
            emptyText: loading
              ? "Loading recorded time entries..."
              : "No time entries recorded for this matter",
          }}
        />
      </div>
    </div>
  );
};

const doColumns = (
  currencySymbol: string,
  feeEarnerLabel: string,
  feeEarnerLevelLabel: string
): ColumnType<AccruedTime>[] => {
  return [
    {
      title: feeEarnerLevelLabel,
      dataIndex: "feeEarnerLevelDescription",
      key: "feeEarnerLevelDescription",
      width: "20rem",
      ellipsis: true,
      sorter: (
        { feeEarnerLevelDescription: lhs }: AccruedTime,
        { feeEarnerLevelDescription: rhs }: AccruedTime
      ) => lhs.localeCompare(rhs),
    },
    {
      title: feeEarnerLabel,
      dataIndex: "feeEarnerName",
      key: "feeEarnerName",
      ellipsis: true,
      sorter: ({ feeEarnerName: lhs }: AccruedTime, { feeEarnerName: rhs }: AccruedTime) =>
        (lhs || "").localeCompare(rhs || ""),
    },
    {
      title: "Time (hrs:min)",
      dataIndex: "actualDuration",
      key: "actualDuration",
      render: (actualDuration: number) => formatTime(actualDuration),
      align: "right",
      width: "15rem",
      sorter: ({ actualDuration: lhs }: AccruedTime, { actualDuration: rhs }: AccruedTime) =>
        lhs - rhs,
    },
    {
      title: "Fee",
      dataIndex: "actualFee",
      key: "actualFee",
      render: (actualFee: number) => formatPrice(actualFee, currencySymbol),
      align: "right",
      width: "15rem",
      sorter: ({ actualFee: lhs }: AccruedTime, { actualFee: rhs }: AccruedTime) => lhs - rhs,
    },
  ];
};
